import {
  Nervous,
  MentalHealth,
  Confused,
  Traumatism,
  Sleepy,
  Death
} from 'healthicons-react';

export const assessmentData = {
  anxiety: {
    id: 'anxiety',
    title: 'Generalized Anxiety Disorder Assessment (GAD-7)',
    icon: Nervous,
    color: 'blue',
    description: 'A validated screening tool to assess anxiety disorder symptoms over the past two weeks',
    maxScore: 21,
    questions: [
      {
        id: 1,
        text: 'Feeling nervous, anxious, or on edge',
        options: [
          { label: 'Not at all', score: 0 },
          { label: 'Several days', score: 1 },
          { label: 'More than half the days', score: 2 },
          { label: 'Nearly every day', score: 3 },
        ],
      },
      {
        id: 2,
        text: 'Not being able to stop or control worrying',
        options: [
          { label: 'Not at all', score: 0 },
          { label: 'Several days', score: 1 },
          { label: 'More than half the days', score: 2 },
          { label: 'Nearly every day', score: 3 },
        ],
      },
      {
        id: 3,
        text: 'Worrying too much about different things',
        options: [
          { label: 'Not at all', score: 0 },
          { label: 'Several days', score: 1 },
          { label: 'More than half the days', score: 2 },
          { label: 'Nearly every day', score: 3 },
        ],
      },
      {
        id: 4,
        text: 'Trouble relaxing',
        options: [
          { label: 'Not at all', score: 0 },
          { label: 'Several days', score: 1 },
          { label: 'More than half the days', score: 2 },
          { label: 'Nearly every day', score: 3 },
        ],
      },
      {
        id: 5,
        text: 'Being so restless that it\'s hard to sit still',
        options: [
          { label: 'Not at all', score: 0 },
          { label: 'Several days', score: 1 },
          { label: 'More than half the days', score: 2 },
          { label: 'Nearly every day', score: 3 },
        ],
      },
      {
        id: 6,
        text: 'Becoming easily annoyed or irritable',
        options: [
          { label: 'Not at all', score: 0 },
          { label: 'Several days', score: 1 },
          { label: 'More than half the days', score: 2 },
          { label: 'Nearly every day', score: 3 },
        ],
      },
      {
        id: 7,
        text: 'Feeling afraid as if something awful might happen',
        options: [
          { label: 'Not at all', score: 0 },
          { label: 'Several days', score: 1 },
          { label: 'More than half the days', score: 2 },
          { label: 'Nearly every day', score: 3 },
        ],
      },
    ],
    scoreRanges: [
      {
        min: 0,
        max: 4,
        level: 'Minimal Anxiety',
        color: 'green',
        interpretation: 'Your responses suggest minimal anxiety symptoms. You appear to be managing well with day-to-day stressors. Continue maintaining your healthy coping strategies and self-care routines.',
        recommendations: [
          'Continue your current wellness practices',
          'Maintain regular exercise and healthy sleep habits',
          'Practice mindfulness or relaxation techniques',
          'Stay connected with supportive friends and family',
        ],
      },
      {
        min: 5,
        max: 9,
        level: 'Mild Anxiety',
        color: 'yellow',
        interpretation: 'Your responses suggest mild anxiety symptoms. While manageable, these symptoms may benefit from attention and some additional coping strategies.',
        recommendations: [
          'Consider speaking with a counselor to learn anxiety management techniques',
          'Practice deep breathing exercises or progressive muscle relaxation',
          'Identify and address sources of stress in your life',
          'Maintain regular physical activity and adequate sleep',
          'Consider joining a support group',
        ],
      },
      {
        min: 10,
        max: 14,
        level: 'Moderate Anxiety',
        color: 'orange',
        interpretation: 'Your responses suggest moderate anxiety that may be interfering with your daily functioning. Professional support is recommended to help you develop effective coping strategies.',
        recommendations: [
          'Schedule a counseling session to discuss treatment options',
          'Consider cognitive-behavioral therapy (CBT) techniques',
          'Learn and practice grounding techniques for acute anxiety',
          'Discuss with a healthcare provider about treatment options',
          'Reduce caffeine and alcohol consumption',
          'Create a structured daily routine',
        ],
      },
      {
        min: 15,
        max: 21,
        level: 'Severe Anxiety',
        color: 'red',
        interpretation: 'Your responses indicate severe anxiety symptoms that are likely significantly impacting your quality of life. Immediate professional help is strongly recommended.',
        recommendations: [
          'Contact a mental health professional as soon as possible',
          'Book an emergency counseling appointment if experiencing crisis',
          'Call Befrienders Kenya (0800 720 648) if you need immediate support',
          'Discuss medication options with a psychiatrist or doctor',
          'Avoid making major life decisions until symptoms improve',
          'Reach out to trusted friends or family for support',
        ],
      },
    ],
  },

  depression: {
    id: 'depression',
    title: 'Depression Screening (PHQ-9)',
    icon: MentalHealth,
    color: 'purple',
    description: 'Patient Health Questionnaire to assess depressive symptoms over the past two weeks',
    maxScore: 27,
    questions: [
      {
        id: 1,
        text: 'Little interest or pleasure in doing things',
        options: [
          { label: 'Not at all', score: 0 },
          { label: 'Several days', score: 1 },
          { label: 'More than half the days', score: 2 },
          { label: 'Nearly every day', score: 3 },
        ],
      },
      {
        id: 2,
        text: 'Feeling down, depressed, or hopeless',
        options: [
          { label: 'Not at all', score: 0 },
          { label: 'Several days', score: 1 },
          { label: 'More than half the days', score: 2 },
          { label: 'Nearly every day', score: 3 },
        ],
      },
      {
        id: 3,
        text: 'Trouble falling or staying asleep, or sleeping too much',
        options: [
          { label: 'Not at all', score: 0 },
          { label: 'Several days', score: 1 },
          { label: 'More than half the days', score: 2 },
          { label: 'Nearly every day', score: 3 },
        ],
      },
      {
        id: 4,
        text: 'Feeling tired or having little energy',
        options: [
          { label: 'Not at all', score: 0 },
          { label: 'Several days', score: 1 },
          { label: 'More than half the days', score: 2 },
          { label: 'Nearly every day', score: 3 },
        ],
      },
      {
        id: 5,
        text: 'Poor appetite or overeating',
        options: [
          { label: 'Not at all', score: 0 },
          { label: 'Several days', score: 1 },
          { label: 'More than half the days', score: 2 },
          { label: 'Nearly every day', score: 3 },
        ],
      },
      {
        id: 6,
        text: 'Feeling bad about yourself or that you are a failure or have let yourself or your family down',
        options: [
          { label: 'Not at all', score: 0 },
          { label: 'Several days', score: 1 },
          { label: 'More than half the days', score: 2 },
          { label: 'Nearly every day', score: 3 },
        ],
      },
      {
        id: 7,
        text: 'Trouble concentrating on things, such as reading or watching television',
        options: [
          { label: 'Not at all', score: 0 },
          { label: 'Several days', score: 1 },
          { label: 'More than half the days', score: 2 },
          { label: 'Nearly every day', score: 3 },
        ],
      },
      {
        id: 8,
        text: 'Moving or speaking so slowly that other people could have noticed, or being so fidgety or restless that you have been moving around a lot more than usual',
        options: [
          { label: 'Not at all', score: 0 },
          { label: 'Several days', score: 1 },
          { label: 'More than half the days', score: 2 },
          { label: 'Nearly every day', score: 3 },
        ],
      },
      {
        id: 9,
        text: 'Thoughts that you would be better off dead or thoughts of hurting yourself in some way',
        options: [
          { label: 'Not at all', score: 0 },
          { label: 'Several days', score: 1 },
          { label: 'More than half the days', score: 2 },
          { label: 'Nearly every day', score: 3 },
        ],
      },
    ],
    scoreRanges: [
      {
        min: 0,
        max: 4,
        level: 'Minimal Depression',
        color: 'green',
        interpretation: 'Your responses suggest minimal or no depressive symptoms. Your mood appears to be within a healthy range.',
        recommendations: [
          'Continue your current wellness practices',
          'Maintain social connections and activities you enjoy',
          'Keep up regular exercise and healthy sleep patterns',
          'Practice gratitude and mindfulness',
        ],
      },
      {
        min: 5,
        max: 9,
        level: 'Mild Depression',
        color: 'yellow',
        interpretation: 'Your responses suggest mild depressive symptoms. These symptoms may benefit from attention and self-care strategies.',
        recommendations: [
          'Consider scheduling a counseling session to discuss your mood',
          'Increase physical activity - even a daily walk can help',
          'Reconnect with supportive friends and loved ones',
          'Establish a regular sleep schedule',
          'Limit alcohol and caffeine consumption',
        ],
      },
      {
        min: 10,
        max: 14,
        level: 'Moderate Depression',
        color: 'orange',
        interpretation: 'Your responses suggest moderate depression that may be affecting your daily life. Professional support is recommended.',
        recommendations: [
          'Schedule an appointment with a mental health counselor',
          'Discuss therapy options like cognitive-behavioral therapy',
          'Talk to your doctor about treatment options',
          'Create a daily routine with small, achievable goals',
          'Avoid isolation - stay connected with others',
          'Consider joining a support group',
        ],
      },
      {
        min: 15,
        max: 19,
        level: 'Moderately Severe Depression',
        color: 'red',
        interpretation: 'Your responses indicate moderately severe depression. It is important to seek professional help promptly.',
        recommendations: [
          'Contact a mental health professional immediately',
          'Discuss both therapy and medication options with a healthcare provider',
          'Inform trusted friends or family about how you\'re feeling',
          'Call Befrienders Kenya (0800 720 648) if you need immediate support',
          'Avoid making major life decisions while experiencing these symptoms',
          'Create a safety plan with your counselor',
        ],
      },
      {
        min: 20,
        max: 27,
        level: 'Severe Depression',
        color: 'red',
        interpretation: 'Your responses indicate severe depression. Immediate professional intervention is critical for your wellbeing and safety.',
        recommendations: [
          'Seek immediate professional help - contact emergency services if needed',
          'Book an emergency counseling appointment right away',
          'Call Befrienders Kenya (0800 720 648) or emergency services (999)',
          'Do not stay alone - reach out to trusted friends or family',
          'Remove access to means of self-harm',
          'Consider hospitalization if you feel unable to keep yourself safe',
        ],
      },
    ],
  },

  stress: {
    id: 'stress',
    title: 'Perceived Stress Scale (PSS-10)',
    icon: Confused,
    color: 'orange',
    description: 'Measure your stress levels over the past month',
    maxScore: 40,
    questions: [
      {
        id: 1,
        text: 'How often have you been upset because of something that happened unexpectedly?',
        options: [
          { label: 'Never', score: 0 },
          { label: 'Almost never', score: 1 },
          { label: 'Sometimes', score: 2 },
          { label: 'Fairly often', score: 3 },
          { label: 'Very often', score: 4 },
        ],
      },
      {
        id: 2,
        text: 'How often have you felt that you were unable to control the important things in your life?',
        options: [
          { label: 'Never', score: 0 },
          { label: 'Almost never', score: 1 },
          { label: 'Sometimes', score: 2 },
          { label: 'Fairly often', score: 3 },
          { label: 'Very often', score: 4 },
        ],
      },
      {
        id: 3,
        text: 'How often have you felt nervous and stressed?',
        options: [
          { label: 'Never', score: 0 },
          { label: 'Almost never', score: 1 },
          { label: 'Sometimes', score: 2 },
          { label: 'Fairly often', score: 3 },
          { label: 'Very often', score: 4 },
        ],
      },
      {
        id: 4,
        text: 'How often have you felt confident about your ability to handle your personal problems?',
        options: [
          { label: 'Very often', score: 0 },
          { label: 'Fairly often', score: 1 },
          { label: 'Sometimes', score: 2 },
          { label: 'Almost never', score: 3 },
          { label: 'Never', score: 4 },
        ],
      },
      {
        id: 5,
        text: 'How often have you felt that things were going your way?',
        options: [
          { label: 'Very often', score: 0 },
          { label: 'Fairly often', score: 1 },
          { label: 'Sometimes', score: 2 },
          { label: 'Almost never', score: 3 },
          { label: 'Never', score: 4 },
        ],
      },
      {
        id: 6,
        text: 'How often have you found that you could not cope with all the things that you had to do?',
        options: [
          { label: 'Never', score: 0 },
          { label: 'Almost never', score: 1 },
          { label: 'Sometimes', score: 2 },
          { label: 'Fairly often', score: 3 },
          { label: 'Very often', score: 4 },
        ],
      },
      {
        id: 7,
        text: 'How often have you been able to control irritations in your life?',
        options: [
          { label: 'Very often', score: 0 },
          { label: 'Fairly often', score: 1 },
          { label: 'Sometimes', score: 2 },
          { label: 'Almost never', score: 3 },
          { label: 'Never', score: 4 },
        ],
      },
      {
        id: 8,
        text: 'How often have you felt that you were on top of things?',
        options: [
          { label: 'Very often', score: 0 },
          { label: 'Fairly often', score: 1 },
          { label: 'Sometimes', score: 2 },
          { label: 'Almost never', score: 3 },
          { label: 'Never', score: 4 },
        ],
      },
      {
        id: 9,
        text: 'How often have you been angered because of things that were outside of your control?',
        options: [
          { label: 'Never', score: 0 },
          { label: 'Almost never', score: 1 },
          { label: 'Sometimes', score: 2 },
          { label: 'Fairly often', score: 3 },
          { label: 'Very often', score: 4 },
        ],
      },
      {
        id: 10,
        text: 'How often have you felt difficulties were piling up so high that you could not overcome them?',
        options: [
          { label: 'Never', score: 0 },
          { label: 'Almost never', score: 1 },
          { label: 'Sometimes', score: 2 },
          { label: 'Fairly often', score: 3 },
          { label: 'Very often', score: 4 },
        ],
      },
    ],
    scoreRanges: [
      {
        min: 0,
        max: 13,
        level: 'Low Stress',
        color: 'green',
        interpretation: 'Your stress levels appear to be low and manageable. You seem to be coping well with life\'s challenges.',
        recommendations: [
          'Continue your current stress management techniques',
          'Maintain work-life balance',
          'Keep up healthy lifestyle habits',
          'Share your coping strategies with others who may benefit',
        ],
      },
      {
        min: 14,
        max: 26,
        level: 'Moderate Stress',
        color: 'yellow',
        interpretation: 'Your stress levels are moderate. This is common, but paying attention to stress management is important.',
        recommendations: [
          'Practice regular relaxation techniques (deep breathing, meditation)',
          'Prioritize tasks and learn to say no when necessary',
          'Ensure adequate sleep and exercise',
          'Consider speaking with a counselor about stress management strategies',
          'Take regular breaks throughout your day',
        ],
      },
      {
        min: 27,
        max: 40,
        level: 'High Stress',
        color: 'red',
        interpretation: 'Your stress levels are high and may be impacting your health and wellbeing. Professional support is recommended.',
        recommendations: [
          'Schedule a counseling session to develop a stress management plan',
          'Identify major stressors and create an action plan to address them',
          'Practice daily relaxation exercises',
          'Consider reducing commitments where possible',
          'Discuss stress-related health concerns with your doctor',
          'Build a strong support network',
        ],
      },
    ],
  },

  ptsd: {
    id: 'ptsd',
    title: 'PTSD Screening (PC-PTSD-5)',
    icon: Traumatism,
    color: 'indigo',
    description: 'Brief screening for post-traumatic stress disorder',
    maxScore: 5,
    questions: [
      {
        id: 1,
        text: 'Have you had nightmares about a stressful experience or thought about it when you did not want to?',
        options: [
          { label: 'No', score: 0 },
          { label: 'Yes', score: 1 },
        ],
      },
      {
        id: 2,
        text: 'Have you tried hard not to think about the stressful experience or went out of your way to avoid situations that reminded you of it?',
        options: [
          { label: 'No', score: 0 },
          { label: 'Yes', score: 1 },
        ],
      },
      {
        id: 3,
        text: 'Have you been constantly on guard, watchful, or easily startled?',
        options: [
          { label: 'No', score: 0 },
          { label: 'Yes', score: 1 },
        ],
      },
      {
        id: 4,
        text: 'Have you felt numb or detached from people, activities, or your surroundings?',
        options: [
          { label: 'No', score: 0 },
          { label: 'Yes', score: 1 },
        ],
      },
      {
        id: 5,
        text: 'Have you felt guilty or unable to stop blaming yourself or others for the stressful experience or what happened after it?',
        options: [
          { label: 'No', score: 0 },
          { label: 'Yes', score: 1 },
        ],
      },
    ],
    scoreRanges: [
      {
        min: 0,
        max: 2,
        level: 'Low PTSD Risk',
        color: 'green',
        interpretation: 'Your responses suggest a low probability of PTSD. However, if you\'ve experienced trauma, monitoring your symptoms is still important.',
        recommendations: [
          'Continue healthy coping strategies',
          'Maintain social connections',
          'Practice self-care and stress management',
          'Seek support if symptoms worsen',
        ],
      },
      {
        min: 3,
        max: 5,
        level: 'Positive PTSD Screen',
        color: 'red',
        interpretation: 'Your responses indicate possible PTSD symptoms. A comprehensive evaluation by a mental health professional is strongly recommended.',
        recommendations: [
          'Schedule an appointment with a trauma-informed counselor',
          'Consider evidence-based treatments like EMDR or trauma-focused CBT',
          'Call Befrienders Kenya (0800 720 648) if you need immediate support',
          'Avoid isolation - connect with supportive people',
          'Practice grounding techniques when experiencing flashbacks',
          'Be patient with yourself - healing takes time',
        ],
      },
    ],
  },

  insomnia: {
    id: 'insomnia',
    title: 'Insomnia Severity Index (ISI)',
    icon: Sleepy,
    color: 'indigo',
    description: 'Assess the nature and severity of insomnia',
    maxScore: 28,
    questions: [
      {
        id: 1,
        text: 'Difficulty falling asleep',
        options: [
          { label: 'None', score: 0 },
          { label: 'Mild', score: 1 },
          { label: 'Moderate', score: 2 },
          { label: 'Severe', score: 3 },
          { label: 'Very severe', score: 4 },
        ],
      },
      {
        id: 2,
        text: 'Difficulty staying asleep',
        options: [
          { label: 'None', score: 0 },
          { label: 'Mild', score: 1 },
          { label: 'Moderate', score: 2 },
          { label: 'Severe', score: 3 },
          { label: 'Very severe', score: 4 },
        ],
      },
      {
        id: 3,
        text: 'Problem waking up too early',
        options: [
          { label: 'None', score: 0 },
          { label: 'Mild', score: 1 },
          { label: 'Moderate', score: 2 },
          { label: 'Severe', score: 3 },
          { label: 'Very severe', score: 4 },
        ],
      },
      {
        id: 4,
        text: 'How satisfied/dissatisfied are you with your current sleep pattern?',
        options: [
          { label: 'Very satisfied', score: 0 },
          { label: 'Satisfied', score: 1 },
          { label: 'Neutral', score: 2 },
          { label: 'Dissatisfied', score: 3 },
          { label: 'Very dissatisfied', score: 4 },
        ],
      },
      {
        id: 5,
        text: 'How noticeable to others do you think your sleeping problem is in terms of impairing your quality of life?',
        options: [
          { label: 'Not at all noticeable', score: 0 },
          { label: 'A little', score: 1 },
          { label: 'Somewhat', score: 2 },
          { label: 'Much', score: 3 },
          { label: 'Very much noticeable', score: 4 },
        ],
      },
      {
        id: 6,
        text: 'How worried/distressed are you about your current sleep problem?',
        options: [
          { label: 'Not at all worried', score: 0 },
          { label: 'A little', score: 1 },
          { label: 'Somewhat', score: 2 },
          { label: 'Much', score: 3 },
          { label: 'Very much worried', score: 4 },
        ],
      },
      {
        id: 7,
        text: 'To what extent do you consider your sleep problem to interfere with your daily functioning?',
        options: [
          { label: 'Not at all interfering', score: 0 },
          { label: 'A little', score: 1 },
          { label: 'Somewhat', score: 2 },
          { label: 'Much', score: 3 },
          { label: 'Very much interfering', score: 4 },
        ],
      },
    ],
    scoreRanges: [
      {
        min: 0,
        max: 7,
        level: 'No Clinically Significant Insomnia',
        color: 'green',
        interpretation: 'Your sleep patterns appear to be within the normal range. Continue your healthy sleep habits.',
        recommendations: [
          'Maintain a consistent sleep schedule',
          'Continue good sleep hygiene practices',
          'Keep your bedroom dark, quiet, and cool',
          'Limit screen time before bed',
        ],
      },
      {
        min: 8,
        max: 14,
        level: 'Subthreshold Insomnia',
        color: 'yellow',
        interpretation: 'You may be experiencing mild insomnia symptoms. Improving sleep hygiene may help.',
        recommendations: [
          'Establish a relaxing bedtime routine',
          'Avoid caffeine and alcohol before bed',
          'Exercise regularly, but not close to bedtime',
          'Consider speaking with a counselor if symptoms persist',
          'Try relaxation techniques like meditation',
        ],
      },
      {
        min: 15,
        max: 21,
        level: 'Moderate Insomnia',
        color: 'orange',
        interpretation: 'You are experiencing moderate insomnia that may benefit from professional intervention.',
        recommendations: [
          'Consult with a healthcare provider about treatment options',
          'Consider Cognitive Behavioral Therapy for Insomnia (CBT-I)',
          'Keep a sleep diary to identify patterns',
          'Avoid daytime napping',
          'Create a sleep-friendly environment',
          'Discuss medication options if needed',
        ],
      },
      {
        min: 22,
        max: 28,
        level: 'Severe Insomnia',
        color: 'red',
        interpretation: 'Your insomnia is severe and likely significantly impacting your quality of life. Professional treatment is strongly recommended.',
        recommendations: [
          'See a healthcare provider or sleep specialist immediately',
          'Discuss both behavioral and medication treatments',
          'Consider a comprehensive sleep study',
          'Address any underlying medical or mental health conditions',
          'Strictly follow sleep hygiene recommendations',
          'Join a support group for insomnia',
        ],
      },
    ],
  },

  suicidal: {
    id: 'suicidal',
    title: 'Suicidal Ideation Screening (C-SSRS)',
    icon: Death,
    color: 'red',
    description: 'Columbia-Suicide Severity Rating Scale for assessing suicidal thoughts and behaviors',
    maxScore: 25,
    questions: [
      {
        id: 1,
        text: 'Have you wished you were dead or wished you could go to sleep and not wake up?',
        options: [
          { label: 'Not at all', score: 0 },
          { label: 'A little bit', score: 1 },
          { label: 'Somewhat', score: 2 },
          { label: 'Very much', score: 3 },
          { label: 'Extremely', score: 4 },
        ],
      },
      {
        id: 2,
        text: 'Have you had actual thoughts of killing yourself?',
        options: [
          { label: 'Not at all', score: 0 },
          { label: 'A little bit', score: 1 },
          { label: 'Somewhat', score: 2 },
          { label: 'Very much', score: 3 },
          { label: 'Extremely', score: 4 },
        ],
      },
      {
        id: 3,
        text: 'Have you been thinking about how you might kill yourself?',
        options: [
          { label: 'Not at all', score: 0 },
          { label: 'A little bit', score: 1 },
          { label: 'Somewhat', score: 2 },
          { label: 'Very much', score: 3 },
          { label: 'Extremely', score: 4 },
        ],
      },
      {
        id: 4,
        text: 'Have you had these thoughts and had some intention of acting on them?',
        options: [
          { label: 'Not at all', score: 0 },
          { label: 'A little bit', score: 1 },
          { label: 'Somewhat', score: 2 },
          { label: 'Very much', score: 3 },
          { label: 'Extremely', score: 4 },
        ],
      },
      {
        id: 5,
        text: 'Have you started to work out or worked out the details of how to kill yourself?',
        options: [
          { label: 'Not at all', score: 0 },
          { label: 'A little bit', score: 1 },
          { label: 'Somewhat', score: 2 },
          { label: 'Very much', score: 3 },
          { label: 'Extremely', score: 4 },
        ],
      },
      {
        id: 6,
        text: 'Do you intend to carry out this plan?',
        options: [
          { label: 'Definitely not', score: 0 },
          { label: 'Probably not', score: 1 },
          { label: 'Unsure', score: 2 },
          { label: 'Probably yes', score: 3 },
          { label: 'Definitely yes', score: 4 },
        ],
      },
    ],
    scoreRanges: [
      {
        min: 0,
        max: 3,
        level: 'Low Risk',
        color: 'green',
        interpretation: 'Your responses suggest low risk of suicidal ideation. However, if you are experiencing any distress, please reach out for support.',
        recommendations: [
          'Continue maintaining healthy coping strategies',
          'Stay connected with supportive friends and family',
          'Practice self-care and stress management',
          'Monitor your mental health regularly',
          'Keep emergency contact numbers accessible',
          'Consider speaking with a counselor for general mental health support',
        ],
      },
      {
        min: 4,
        max: 8,
        level: 'Moderate Risk',
        color: 'orange',
        interpretation: 'Your responses indicate some concerning thoughts. It is important to speak with a mental health professional as soon as possible.',
        recommendations: [
          'Schedule an appointment with a counselor or therapist immediately',
          'Share your feelings with a trusted friend or family member',
          'Call Befrienders Kenya (0800 720 648) for immediate support',
          'Remove access to means of self-harm',
          'Create a safety plan with specific coping strategies',
          'Avoid being alone if possible',
          'Consider keeping a thought journal to identify triggers',
        ],
      },
      {
        min: 9,
        max: 25,
        level: 'High Risk - Immediate Help Needed',
        color: 'red',
        interpretation: 'Your responses indicate serious suicidal thoughts that require immediate professional intervention. Your safety is the top priority.',
        recommendations: [
          'CALL EMERGENCY SERVICES (999/112) OR GO TO THE NEAREST EMERGENCY ROOM IMMEDIATELY',
          'Call Befrienders Kenya Crisis Line: 0800 720 648 (24/7 toll-free)',
          'Do not stay alone - contact a trusted person right now',
          'Remove all potential means of self-harm from your environment',
          'Tell someone you trust about these feelings immediately',
          'Book an emergency counseling session',
          'Consider voluntary hospitalization for your safety',
          'Text "HELLO" to 0722 178 177 for crisis support',
        ],
      },
    ],
  },
};

// Helper function to get all assessment types
export const getAssessmentTypes = () => Object.keys(assessmentData);

// Helper function to get assessment info for selector
export const getAssessmentsList = () => {
  return Object.values(assessmentData).map(assessment => ({
    id: assessment.id,
    title: assessment.title,
    icon: assessment.icon,
    color: assessment.color,
    description: assessment.description,
  }));
};
